import { memo, useState } from 'react';
import { MapPin, ImageIcon, Sparkles, AlertTriangle, CheckCircle2 } from 'lucide-react';

import { Dialog } from '../../../components/ui/Dialog';
import { SwiperImages } from '../../../components/ui/SwiperImages';

const fmtPrice = (n) =>
  n == null ? '—' : `${Number(n).toLocaleString('fr-FR')} €`;

// State → visual pill.
const STATE_PILLS = {
  new: {
    label: 'New',
    className: 'bg-greenColor/15 text-greenColor',
    Icon: Sparkles,
  },
  changed: {
    label: 'Changed',
    className: 'bg-orangeColor/15 text-orangeColor',
    Icon: AlertTriangle,
  },
  unchanged: {
    label: 'Unchanged',
    className: 'bg-lightGrayColor text-darkGrayColor',
    Icon: CheckCircle2,
  },
  duplicate: {
    label: 'Duplicate',
    className: 'bg-lightGrayColor text-darkGrayColor',
    Icon: CheckCircle2,
  },
};

const MODE_LABELS = {
  create: 'Create',
  enrich: 'Enrich',
  overwrite: 'Overwrite',
  skip: 'Skip',
};

const MODE_STYLES = {
  create: 'bg-greenColor text-white',
  enrich: 'bg-purpleColor text-white',
  overwrite: 'bg-orangeColor text-white',
  skip: 'bg-lightGrayColor text-darkGrayColor',
};

// One preview card. Kept memo'd — the virtualized grid can render up to
// 3 rows on screen at a time and we don't want re-renders when parent
// state ticks unrelated to this row.
const RecordCardInner = ({
  record,
  decision,
  onDecisionChange,
  onOpenDiff,
  readOnly = false,
}) => {
  const [lightbox, setLightbox] = useState(false);

  const state = record.state || 'new';
  const pill = STATE_PILLS[state] || STATE_PILLS.new;
  const mode = decision?.mode;
  const isSelected = mode && mode !== 'skip';

  const availableModes =
    state === 'new'
      ? ['create', 'skip']
      : ['enrich', 'overwrite', 'skip'];

  const setMode = (m) => {
    if (m === 'enrich' && state === 'changed') {
      onOpenDiff?.(record);
      return;
    }
    if (m === mode) return;

    const decisionUpdate = { mode: m };
    if (m === 'enrich') {
      decisionUpdate.selected_fields = [
        'price',
        'photos',
        'features',
        'agency_phone',
      ];
    }
    onDecisionChange?.(record.parser_advert_id, decisionUpdate);
  };

  return (
    <>
      <article
        data-testid={`preview-record-card-${record.parser_advert_id}`}
        className={
          'flex flex-col w-full h-full rounded-lg border transition-all hover:shadow-md focus-within:ring-2 focus-within:ring-purpleColor overflow-hidden bg-white ' +
          (isSelected ? 'border-purpleColor' : 'border-gray-200')
        }
      >
        <button
          type="button"
          className="relative w-full aspect-[4/3] bg-lightGrayColor overflow-hidden group"
          onClick={() => setLightbox(true)}
          aria-label={`Open ${record.title} photos`}
        >
          {record.hero_photo_url ? (
            <img
              src={record.hero_photo_url}
              alt={record.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              loading="lazy"
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-1 w-full h-full text-darkGrayColor">
              <ImageIcon size={24} />
              <span className="text-xs">No photo</span>
            </div>
          )}
          {record.photo_count > 1 && (
            <span className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded bg-black/60 text-white text-[10px]">
              <ImageIcon size={10} />
              {record.photo_count}
            </span>
          )}
          <span
            data-testid={`preview-record-state-${state}`}
            className={
              'absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ' +
              pill.className
            }
          >
            <pill.Icon size={10} />
            {pill.label}
            {state === 'changed' && record.diff_summary?.change_count > 0 && (
              <span className="ml-1 px-1 rounded bg-white/40">
                {record.diff_summary.change_count}
              </span>
            )}
          </span>
          {record.source && (
            <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-white/90 text-blackColor text-[10px]">
              {record.source.replace(/_spider$/, '')}
            </span>
          )}
        </button>

        <div className="flex flex-col gap-2 p-3 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-medium text-blackColor line-clamp-2">
              {record.title}
            </h3>
            <span className="text-sm font-semibold text-purpleColor whitespace-nowrap">
              {fmtPrice(record.current_price)}
            </span>
          </div>

          <div className="flex items-center gap-1 text-xs text-darkGrayColor">
            <MapPin size={12} />
            <span className="truncate">{record.city}</span>
            {record.nice_distance_km != null && (
              <span className="text-darkGrayColor/60">
                · {Number(record.nice_distance_km).toFixed(1)} km
              </span>
            )}
          </div>

          {record.top_features && record.top_features.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {record.top_features.slice(0, 4).map((f) => (
                <span
                  key={f}
                  className="px-1.5 py-0.5 text-[10px] rounded bg-lightGrayColor text-darkGrayColor"
                >
                  {f}
                </span>
              ))}
            </div>
          )}

          {!readOnly && state === 'changed' && onOpenDiff && (
            <button
              type="button"
              data-testid={`diff-open-${record.parser_advert_id}`}
              className="text-xs text-purpleColor hover:underline text-left"
              onClick={() => onOpenDiff(record)}
            >
              Show {record.diff_summary?.change_count ?? ''} changed fields →
            </button>
          )}

          {!readOnly && (
            <div className="mt-auto flex items-center gap-1 flex-wrap pt-2 border-t border-gray-100">
              {availableModes.map((m) => {
                const isActive = m === mode;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={
                      'px-2 py-1 text-[11px] rounded border transition-colors ' +
                      (isActive
                        ? MODE_STYLES[m] + ' border-transparent'
                        : 'border-gray-200 text-darkGrayColor hover:border-purpleColor')
                    }
                  >
                    {MODE_LABELS[m]}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </article>

      <Dialog
        isOpen={lightbox}
        onClose={() => setLightbox(false)}
        classes="w-[90vw] max-w-4xl"
        content={
          <div className="relative w-full min-h-[60vh]">
            {record.photos && record.photos.length > 0 ? (
              <SwiperImages realtyId={record.parser_advert_id}>
                {record.photos.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-center w-full h-[60vh] bg-black"
                  >
                    <img
                      src={p}
                      alt={`${record.title} ${i + 1}`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ))}
              </SwiperImages>
            ) : (
              <div className="flex items-center justify-center py-20 text-darkGrayColor">
                No photos available
              </div>
            )}
          </div>
        }
      />
    </>
  );
};

export const RecordCard = memo(RecordCardInner);
